let listener = null;
const PREFIX = 'on';

class State {
	/** @type Map<number, Set<({fn:function, options:object, lastUpdateEt:number, instance:any})>> */
	#listeners = new Map();

	#defaultOptions = {
		fps: Infinity,
	};

	/**
	 *
	 * @param {Record<string, number>} eventsConstants
	 */
	constructor(eventsConstants = {}) {
		const eventsMap = Object.fromEntries(
			Object.entries(eventsConstants).map(([key, value]) => [
				value,
				`${PREFIX}${key
					.toLowerCase()
					.split('_')
					.map((str) => str.charAt(0).toUpperCase() + str.slice(1))
					.join('')}`,
			]),
		);

		this.eventsMapValues = Object.values(eventsMap);
		this.eventsMapKey = Object.fromEntries(Object.entries(eventsMap).map((e) => [e[1], parseInt(e[0])]));
	}

	/**
	 *
	 * @param {*} instance the class instance
	 */
	register(instance, options = {}) {
		getAllMethodsNames(instance).forEach((propertyName) => {
			if (this.eventsMapValues.includes(propertyName)) {
				this.on(this.eventsMapKey[propertyName], instance[propertyName], options, instance);
			}
		});
	}

	/**
	 *
	 * @param {*} instance the class instance
	 */
	unregister(instance) {
		getAllMethodsNames(instance).forEach((propertyName) => {
			if (this.eventsMapValues.includes(propertyName)) {
				this.off(this.eventsMapKey[propertyName], instance[propertyName], instance);
			}
		});
	}

	/**
	 *
	 * @param {number} id defined in the constants file
	 * @param {(...params:any[]) => any} fn callback function
	 * @param {object} options overwrite default options
	 */
	on(id, fn, options = {}, instance = this) {
		if (!this.#listeners.has(id)) this.#listeners.set(id, new Set());

		this.#listeners
			.get(id)
			.add({ fn, options: { ...this.#defaultOptions, ...options }, instance, lastUpdateEt: 0 });
	}

	/**
	 *
	 * @param {number} id defined in the constants file
	 * @param {(...any)=> any} fn callback function
	 * @returns
	 */
	off(id, fn, instance = this) {
		if (!this.#listeners.has(id)) return;

		for (listener of this.#listeners.get(id)) {
			if (listener.fn === fn && listener.instance === instance) {
				this.#listeners.get(id).delete(listener);
			}
		}
	}

	/**
	 *
	 * @param {number} id defined in the constants file
	 * @param  {...any} args event payload
	 */
	emit(id, ...args) {
		if (this.#listeners.has(id))
			for (listener of this.#listeners.get(id)) listener.fn.call(listener.instance, ...args);
	}

	/**
	 *
	 * @param {number} id defined in the constants file
	 * @param  {{dt: number, et: number} } params ticker params
	 * @param  {...any} args event payload
	 */
	emitThrottle(id, params, ...args) {
		if (this.#listeners.has(id)) {
			for (listener of this.#listeners.get(id)) {
				if (listener.options?.fps && listener.options.fps !== Infinity) {
					if (params.et - listener.lastUpdateEt >= 1 / listener.options.fps) {
						listener.fn.call(listener.instance, params, ...args);
						listener.lastUpdateEt = params.et;
					}
				} else listener.fn.call(listener.instance, params, ...args);
			}
		}
	}
}

const isFunction = (instance, propertyName) => {
	return (
		!('get' in Object.getOwnPropertyDescriptor(instance, propertyName)) &&
		typeof instance[propertyName] === 'function'
	);
};

const getAllMethodsNames = (object, includeObjectPrototype = false) => {
	const props = Object.getOwnPropertyNames(object);

	let proto = Object.getPrototypeOf(object);
	const objectProto = Object.getPrototypeOf({});

	while (proto && (includeObjectPrototype || proto !== objectProto)) {
		for (const prop of Object.getOwnPropertyNames(proto)) {
			if (props.indexOf(prop) === -1 && isFunction(proto, prop)) {
				props.push(prop);
			}
		}
		proto = Object.getPrototypeOf(proto);
	}

	return props.filter((prop) => prop.startsWith(PREFIX));
};

export { State };
